import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

function HeroCarousel() {
  const [carouselItems, setCarouselItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    productId: '',
    title: '',
    imageUrl: '',
    imageFile: null,
    previewUrl: '', // For preview only
    order: 0,
    isActive: true,
    page: 'homepage' // Default to homepage
  });
  const [selectedPage, setSelectedPage] = useState('all'); // Filter for admin view

  useEffect(() => {
    fetchCarouselItems();
    fetchProducts();
  }, [selectedPage]);

  const fetchCarouselItems = async () => {
    try {
      const url = selectedPage === 'all' 
        ? `${config.API_URL}/hero-carousel/admin/all`
        : `${config.API_URL}/hero-carousel/admin/all?page=${selectedPage}`;
      const response = await axios.get(url);
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setCarouselItems(response.data);
      } else {
        console.warn('Carousel API returned non-array data:', response.data);
        setCarouselItems([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching carousel items:', error);
      setCarouselItems([]);
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch only products with stock > 0 (available products)
      const response = await axios.get(`${config.API_URL}/products?limit=1000`);
      // Filter products to show only those with stock > 0
      const availableProducts = response.data.filter(product => product.stock > 0);
      setProducts(availableProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({
      productId: '',
      title: '',
      imageUrl: '',
      imageFile: null,
      previewUrl: '',
      order: carouselItems.length,
      isActive: true,
      page: selectedPage !== 'all' ? selectedPage : 'homepage'
    });
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      productId: item.productId?._id || item.productId || '',
      title: item.title || '',
      imageUrl: item.imageUrl || '',
      imageFile: null,
      previewUrl: item.imageUrl || '',
      order: item.order || 0,
      isActive: item.isActive !== undefined ? item.isActive : true,
      page: item.page || 'homepage'
    });
    setShowAddModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('Image size should be less than 5MB. Please compress the image.');
        setTimeout(() => setMessage(''), 5000);
        e.target.value = ''; // Clear file input
        return;
      }
      setFormData({ ...formData, imageFile: file, imageUrl: '' }); // Clear base64 URL, use file instead
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, imageFile: file, previewUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Product ID is optional - only required if user wants to link to a product
    // For welcome/main page images, product can be empty

    if (!formData.imageFile && !formData.imageUrl) {
      setMessage('Please upload an image file or provide image URL');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Don't send base64 data URL if file is selected (to avoid 413 error)
    if (formData.imageFile && formData.imageUrl && formData.imageUrl.startsWith('data:')) {
      // Clear base64 URL, use file instead
      setFormData(prev => ({ ...prev, imageUrl: '' }));
    }

    const pageType = formData.page || 'homepage';
    const pageItems = carouselItems.filter(item => item.page === pageType);
    if (pageItems.length >= 10 && !editingItem) {
      setMessage(`Maximum 10 carousel items allowed for ${pageType} page!`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const submitData = new FormData();
      submitData.append('productId', formData.productId || '');
      submitData.append('title', formData.title || '');
      submitData.append('order', formData.order);
      submitData.append('isActive', formData.isActive);
      submitData.append('page', formData.page || 'homepage');
      
      // Always prefer file upload over base64 URL to avoid 413 errors
      if (formData.imageFile) {
        submitData.append('image', formData.imageFile);
      } else if (formData.imageUrl && !formData.imageUrl.startsWith('data:')) {
        // Only use URL if it's not a base64 data URL
        submitData.append('imageUrl', formData.imageUrl);
      } else if (editingItem && editingItem.imageUrl) {
        // When editing, if no new file and no URL provided, keep existing image
        submitData.append('imageUrl', editingItem.imageUrl);
      } else {
        throw new Error('Please upload an image file');
      }

      if (editingItem) {
        const response = await axios.put(`${config.API_URL}/hero-carousel/${editingItem._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        console.log('✅ Carousel update response:', response.data);
        console.log('✅ Updated imageUrl:', response.data.imageUrl);
        setMessage('Carousel item updated successfully!');
      } else {
        await axios.post(`${config.API_URL}/hero-carousel`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage('Carousel item added successfully!');
      }

      setTimeout(() => {
        setMessage('');
        setShowAddModal(false);
        setEditingItem(null);
        setFormData({
          productId: '',
          title: '',
          imageUrl: '',
          imageFile: null,
          previewUrl: '',
          order: 0,
          isActive: true,
          page: 'homepage'
        });
        fetchCarouselItems();
      }, 1500);
    } catch (error) {
      console.error('Error saving carousel item:', error);
      setMessage(error.response?.data?.message || 'Error saving carousel item');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carousel item?')) {
      return;
    }

    try {
      await axios.delete(`${config.API_URL}/hero-carousel/${id}`);
      setMessage('Carousel item deleted successfully!');
      setTimeout(() => {
        setMessage('');
        fetchCarouselItems();
      }, 1500);
    } catch (error) {
      console.error('Error deleting carousel item:', error);
      setMessage('Error deleting carousel item');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleReorder = async (dragIndex, hoverIndex) => {
    const newItems = [...carouselItems];
    const draggedItem = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);
    
    // Update order numbers
    const reorderedItems = newItems.map((item, index) => ({
      id: item._id,
      order: index
    }));

    try {
      await axios.post(`${config.API_URL}/hero-carousel/reorder`, { items: reorderedItems });
      setCarouselItems(newItems.map((item, index) => ({ ...item, order: index })));
    } catch (error) {
      console.error('Error reordering:', error);
      setMessage('Error reordering items');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${config.API_URL}/hero-carousel/${item._id}`, {
        isActive: !item.isActive
      });
      fetchCarouselItems();
    } catch (error) {
      console.error('Error toggling active status:', error);
      setMessage('Error updating status');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-carousel-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>🖼️ Hero Carousel Management</h2>
        <div className="d-flex gap-2 align-items-center">
          <select 
            className="form-select"
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="all">All Pages</option>
            <option value="homepage">Homepage</option>
            <option value="old-mobiles">Old Mobiles</option>
          </select>
          <button 
            className="btn btn-primary"
            onClick={handleAddNew}
            disabled={carouselItems.filter(item => item.page === (selectedPage !== 'all' ? selectedPage : 'homepage')).length >= 10}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add New ({carouselItems.filter(item => selectedPage === 'all' || item.page === selectedPage).length}/10)
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.includes('successfully') ? 'alert-success' : 'alert-danger'} alert-dismissible fade show`}>
          {message}
        </div>
      )}

      {carouselItems.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-images fs-1 text-muted"></i>
            <p className="text-muted mt-3">No carousel items yet. Click "Add New" to create one.</p>
          </div>
        </div>
      ) : (
        <div className="row">
          {carouselItems.map((item, index) => {
            const filteredItems = carouselItems.filter(i => selectedPage === 'all' || i.page === selectedPage);
            const itemIndex = filteredItems.findIndex(i => i._id === item._id);
            if (selectedPage !== 'all' && item.page !== selectedPage) return null;
            return (
            <div key={item._id} className="col-md-6 col-lg-4 mb-4">
              <div className={`card ${!item.isActive ? 'opacity-50' : ''}`}>
                <div className="position-relative">
                  <img 
                    src={item.imageUrl ? item.imageUrl.replace(/^http:\/\//, 'https://').replace(/admin\.a2ztak\.in/, 'a2ztak.in') : ''} 
                    alt={item.title || 'Carousel'} 
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                      // Try to fix URL if image fails to load
                      const currentSrc = e.target.src;
                      const fixedSrc = currentSrc
                        .replace(/^http:\/\//, 'https://')
                        .replace(/admin\.a2ztak\.in/, 'a2ztak.in');
                      if (currentSrc !== fixedSrc) {
                        e.target.src = fixedSrc;
                      }
                    }}
                  />
                  <div className="position-absolute top-0 end-0 m-2">
                    <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="position-absolute top-0 start-0 m-2">
                    <span className="badge bg-primary">#{item.order + 1}</span>
                  </div>
                  <div className="position-absolute bottom-0 start-0 m-2">
                    <span className="badge bg-info">{item.page === 'old-mobiles' ? 'Old Mobiles' : 'Homepage'}</span>
                  </div>
                </div>
                <div className="card-body">
                  <h6 className="card-title">
                    {item.productId?.name || (item.productId ? 'Product' : 'No Product Link')}
                  </h6>
                  {item.title && (
                    <p className="card-text text-muted small">{item.title}</p>
                  )}
                  <div className="d-flex gap-2 flex-wrap">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(item)}
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                    <button 
                      className={`btn btn-sm ${item.isActive ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                      onClick={() => toggleActive(item)}
                    >
                      <i className={`bi ${item.isActive ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      {item.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(item._id)}
                    >
                      <i className="bi bi-trash"></i> Delete
                    </button>
                  </div>
                  {itemIndex > 0 && (
                    <button 
                      className="btn btn-sm btn-outline-info mt-2"
                      onClick={() => handleReorder(index, index - 1)}
                    >
                      <i className="bi bi-arrow-up"></i> Move Up
                    </button>
                  )}
                  {itemIndex < filteredItems.length - 1 && (
                    <button 
                      className="btn btn-sm btn-outline-info mt-2 ms-2"
                      onClick={() => handleReorder(index, index + 1)}
                    >
                      <i className="bi bi-arrow-down"></i> Move Down
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingItem ? 'Edit Carousel Item' : 'Add New Carousel Item'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Page Type *</label>
                    <select
                      className="form-select"
                      value={formData.page}
                      onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                      required
                    >
                      <option value="homepage">Homepage</option>
                      <option value="old-mobiles">Old Mobiles Page</option>
                    </select>
                    <small className="text-muted">Select which page this carousel will appear on</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Select Product (Optional)</label>
                    <select
                      className="form-select"
                      value={formData.productId}
                      onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                    >
                      <option value="">None (Welcome/Main Page Image)</option>
                      {products.map(product => (
                        <option key={product._id} value={product._id}>
                          {product.name} - ₹{product.offerPrice || product.price}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Leave empty for welcome/main page images</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Title (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter title for this carousel item"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Upload Image *</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {(formData.previewUrl || formData.imageUrl) && (
                      <div className="mt-2">
                        <img 
                          src={(formData.previewUrl || formData.imageUrl)?.replace(/^http:\/\//, 'https://').replace(/admin\.a2ztak\.in/, 'a2ztak.in')} 
                          alt="Preview" 
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            const fixedSrc = currentSrc
                              .replace(/^http:\/\//, 'https://')
                              .replace(/admin\.a2ztak\.in/, 'a2ztak.in');
                            if (currentSrc !== fixedSrc) {
                              e.target.src = fixedSrc;
                            }
                          }}
                        />
                      </div>
                    )}
                    <small className="text-muted">Or enter image URL below</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Image URL (Alternative)</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        id="isActive"
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active (Show on homepage)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : editingItem ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeroCarousel;

